import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { cities } from "../helpers/config";
import {
  scheduleKey,
  addressKey,
  addressIdKey,
  streetsUrl,
  numbersUrl,
  scheduleURL,
  yearsUrl,
  buildCode,
  FORCE_CLEAN_DATE_STRING,
  LAST_CLEAN_TIMESTAMP_KEY_STRING,
  FORCE_SCHEDULE_REFRESH_REQUESTED_KEY,
  SCHEDULE_EXPIRATION_MS,
} from "../helpers/config";
import { normalizeDate } from "../helpers/normalizeDate";

type YearEntry = {
  id: string;
  value: string;
};

// Pobiera ID wybranego roku (z localStorage lub bieżący rok)
async function getCurrentYearId(selectedYearId: string | null): Promise<string | null> {
  if (!yearsUrl) return null;
  
  try {
    const { data } = await axios.get<YearEntry[]>(yearsUrl);
    
    // Jeśli jest wybrany rok, użyj go
    if (selectedYearId) {
      const selectedYearEntry = data.find((entry) => entry.id === selectedYearId);
      if (selectedYearEntry) {
        return selectedYearEntry.id;
      }
    }
    
    // Sprawdź czy jest zapisany rok w localStorage
    const savedYearId = localStorage.getItem(SELECTED_YEAR_KEY);
    if (savedYearId) {
      const savedYearEntry = data.find((entry) => entry.id === savedYearId);
      if (savedYearEntry) {
        return savedYearEntry.id;
      }
    }
    
    // Jeśli nie ma zapisanego roku, użyj bieżącego roku
    const currentYear = new Date().getFullYear().toString();
    const yearEntry = data.find((entry) => entry.value === currentYear);
    if (yearEntry) {
      localStorage.setItem(SELECTED_YEAR_KEY, yearEntry.id);
      return yearEntry.id;
    }
    
    // Jeśli bieżący rok nie został znaleziony, weź pierwszy dostępny
    if (data.length > 0) {
      localStorage.setItem(SELECTED_YEAR_KEY, data[0].id);
      return data[0].id;
    }
    
    return null;
  } catch (err) {
    console.error("Failed to fetch years:", err);
    return null;
  }
}

type ItemIdValue = {
  id: string | number;
  value: string;
};
type Street = ItemIdValue;
type Address = ItemIdValue;
type WasteType = "MIXED" | "PAPER" | "PLASTIC" | "GLASS" | "TREE" | "BIG" | "BIO";
type WasteCollectionEntry = {
  date: string; // Format: YYYY-MM-DD
  types: WasteType[];
};
type WasteCollectionSchedule = WasteCollectionEntry[];
type SavedSchedule = {
  data: WasteCollectionSchedule;
  timestamp?: number;
  yearId?: string; // ID roku dla którego został zapisany harmonogram
};

// TODO add this to API
function removeSpacesFromDates(input: SavedSchedule): SavedSchedule {
  return {
    data: input.data
      .map((entry: WasteCollectionEntry) => ({
        ...entry,
        date: normalizeDate(entry.date),
      }))
      .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime()), // Sort dates in ascending order
  };
}

function isScheduleValid(savedSchedule: SavedSchedule | null, currentAddressId: string | null, currentYearId: string | null): boolean {
  if (!savedSchedule || !savedSchedule.timestamp) return false;
  const { timestamp, yearId } = savedSchedule;
  const storedAddressId = localStorage.getItem(addressIdKey);

  // Sprawdź czy rok się zgadza
  if (currentYearId && yearId && yearId !== currentYearId) {
    return false; // Cache jest dla innego roku
  }

  // Data wymuszonego czyszczenia
  const FORCE_CLEAN_DATE = new Date(FORCE_CLEAN_DATE_STRING).getTime();
  // Klucz w localStorage do zapisu daty ostatniego czyszczenia
  const LAST_CLEAN_TIMESTAMP_KEY = LAST_CLEAN_TIMESTAMP_KEY_STRING;
  // Odczyt daty ostatniego czyszczenia
  const lastCleanTimestamp = parseInt(localStorage.getItem(LAST_CLEAN_TIMESTAMP_KEY) || "0", 10);

  // Jeśli obecna data jest późniejsza niż data czyszczenia, ale zapisany timestamp jest starszy niż ta data
  if (Date.now() >= FORCE_CLEAN_DATE && lastCleanTimestamp < FORCE_CLEAN_DATE) {
    localStorage.setItem(LAST_CLEAN_TIMESTAMP_KEY, Date.now().toString()); // Zapisz datę czyszczenia
    return false;
  }

  return (
    storedAddressId === currentAddressId &&
    Date.now() - timestamp < SCHEDULE_EXPIRATION_MS
  );
}

const SELECTED_YEAR_KEY = "smieciarkajedzie_selected_year";

export function useGetSchedule() {
  const [cityId, setCityId] = useState<string | null>(null);
  const [streets, setStreets] = useState<Street[] | null>(null);
  const [streetId, setStreetId] = useState<string | null>(null);
  const [streetName, setStreetName] = useState<string | null>(null);
  const [addresses, setAddresses] = useState<Address[] | null>(null);
  const [addressId, setAddressId] = useState<string | null>(
    () => localStorage.getItem(addressIdKey)?.split(',')[0] || null
  );
  const [addressLabel, setAddressLabel] = useState<string | null>(null);
  const [fullAddress, setFullAddress] = useState<string | null>(
    () => localStorage.getItem(addressKey) || null
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [schedule, setSchedule] = useState<any>(
    () => {
      const savedSchedule: SavedSchedule | null = JSON.parse(localStorage.getItem(scheduleKey) || "null");
      const savedYearId = localStorage.getItem(SELECTED_YEAR_KEY);
      if (savedSchedule && isScheduleValid(savedSchedule, localStorage.getItem(addressIdKey), savedYearId)) {
        return removeSpacesFromDates(savedSchedule).data;
      }
      return [];
    }
  );
  const [availableYears, setAvailableYears] = useState<YearEntry[]>([]);
  const [selectedYearId, setSelectedYearId] = useState<string | null>(() => {
    return localStorage.getItem(SELECTED_YEAR_KEY) || null;
  });

  const formattedCities = useMemo(
    () =>
      cities.map((item: ItemIdValue) => ({
        value: item.id,
        label: item.value,
      })),
    []
  );

  const formattedStreets = useMemo(
    () =>
      streets?.map((item: Street) => ({
        value: item.id,
        label: item.value,
      })) || null,
    [streets]
  );

  const formattedNumbers = useMemo(
    () =>
      addresses?.map((item: Address) => ({
        value: item.id,
        label: item.value,
      })) || null,
    [addresses]
  );

  const fetchStreets = useCallback(async () => {
    if (!cityId) return;
    try {
      const { data } = await axios.get(`${streetsUrl}/${cityId}`);
      setStreets(data);
    } catch (err: any) {
      setError(true);
      throw new Error(`Failed to fetch streets for city ID: ${cityId} - ${err.message}, ${buildCode}`);
    }
  }, [cityId]);

  const fetchAddresses = useCallback(async () => {
    if (!cityId || !streetId) return;
    try {
      const { data } = await axios.get(`${numbersUrl}/${cityId}/${streetId}`);
      setAddresses(data);
    } catch (err: any) {
      setError(true);
      throw new Error(`Failed to fetch addresses for city ID: ${cityId}, street ID: ${streetId} - ${err.message}, ${buildCode}`);
    }
  }, [cityId, streetId]);

  const resetModalState = useCallback(() => {
    setCityId(null);
    setStreetId(null);
    setAddressId(null);
    setAddresses(null);
  }, []);

  // Pobierz dostępne lata przy starcie
  useEffect(() => {
    if (yearsUrl) {
      axios.get<YearEntry[]>(yearsUrl)
        .then(({ data }) => {
          setAvailableYears(data);
          
          // Sprawdź czy zapisany rok jest nadal dostępny
          const savedYearId = localStorage.getItem(SELECTED_YEAR_KEY);
          if (savedYearId) {
            const savedYearEntry = data.find((entry) => entry.id === savedYearId);
            if (savedYearEntry) {
              // Zapisany rok jest dostępny - użyj go
              setSelectedYearId(savedYearEntry.id);
            } else {
              // Zapisany rok nie jest już dostępny - użyj bieżącego roku
              const currentYear = new Date().getFullYear().toString();
              const yearEntry = data.find((entry) => entry.value === currentYear);
              if (yearEntry) {
                setSelectedYearId(yearEntry.id);
                localStorage.setItem(SELECTED_YEAR_KEY, yearEntry.id);
              } else if (data.length > 0) {
                setSelectedYearId(data[0].id);
                localStorage.setItem(SELECTED_YEAR_KEY, data[0].id);
              }
            }
          } else {
            // Jeśli nie ma zapisanego roku, ustaw bieżący rok
            const currentYear = new Date().getFullYear().toString();
            const yearEntry = data.find((entry) => entry.value === currentYear);
            if (yearEntry) {
              setSelectedYearId(yearEntry.id);
              localStorage.setItem(SELECTED_YEAR_KEY, yearEntry.id);
            } else if (data.length > 0) {
              setSelectedYearId(data[0].id);
              localStorage.setItem(SELECTED_YEAR_KEY, data[0].id);
            }
          }
        })
        .catch((err) => {
          console.error("Failed to fetch years:", err);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Funkcja pomocnicza do pobierania harmonogramu dla konkretnego roku
  const fetchScheduleForYear = useCallback(async (addrId: string, yearIdToUse: string | null) => {
    const savedSchedule: SavedSchedule | null = JSON.parse(localStorage.getItem(scheduleKey) || "null");

    // Sprawdź cache tylko jeśli jest zapisany rok i pasuje do wybranego roku
    if (savedSchedule && isScheduleValid(savedSchedule, addrId, yearIdToUse)) {
        setSchedule(removeSpacesFromDates(savedSchedule).data);
        return;
    }

    const MAX_RETRIES = 3;
    let attempt = 0;

    setIsLoading(true);
    try {
        while (attempt < MAX_RETRIES) {
            try {
                const params: { id: string; yearId?: string; cityId?: string; streetId?: string } = { id: addrId };
                if (yearIdToUse) {
                    params.yearId = yearIdToUse;
                }
                // Dla Hemar API wysyłamy cityId i streetId, żeby backend nie musiał przeszukiwać wszystkich adresów
                if (yearsUrl && cityId && streetId) {
                    params.cityId = cityId;
                    params.streetId = streetId;
                }
                const { data } = await axios.get(scheduleURL, { params });
                // Przetwarzanie danych po udanym żądaniu
                const formattedData = removeSpacesFromDates(data).data || [];
                setSchedule(formattedData);

                const scheduleToSave: SavedSchedule = {
                    data: formattedData,
                    timestamp: Date.now(),
                    yearId: yearIdToUse || undefined, // Zapisz ID roku w cache
                };

                localStorage.setItem(scheduleKey, JSON.stringify(scheduleToSave));
                localStorage.setItem(addressIdKey, addrId);

                if (streetName && addressLabel) {
                    const fullAddr = `ul. ${streetName} ${addressLabel}`;
                    localStorage.setItem(addressKey, fullAddr);
                    setFullAddress(fullAddr);
                }

                setError(false);
                return; // Zakończ pętlę po sukcesie
            } catch (err: any) {
                if (err.message === "Network Error") {
                    attempt += 1;
                    if (attempt >= MAX_RETRIES) {
                        setError(true);
                        break; // Zakończ pętlę, ale nie rzucaj błędu
                    }
                } else {
                  throw new Error(`Failed to fetch schedule - ${err.message}, ${buildCode}`);
                }
            }
        }
    } catch (err: any) {
        setError(true);
        throw new Error(`Failed to fetch schedule - ${err.message}, ${buildCode}`);
    } finally {
        resetModalState();
        setIsLoading(false);
    }
  }, [cityId, streetId, streetName, addressLabel, resetModalState]);

  const fetchSchedule = useCallback(async () => {
    if (!addressId) return;

    // Pobierz yearId używając wybranego roku
    const yearId = await getCurrentYearId(selectedYearId);
    
    // Użyj funkcji pomocniczej
    await fetchScheduleForYear(addressId, yearId);
  }, [addressId, selectedYearId, fetchScheduleForYear]);

  useEffect(() => {
    fetchStreets();
  }, [fetchStreets]);

  useEffect(() => {
    fetchAddresses();
  }, [fetchAddresses]);

  useEffect(() => {
    if (addressId) {
      // Wymuszone odświeżenie z ustawień – wyczyść cache i pobierz harmonogram na nowo
      if (localStorage.getItem(FORCE_SCHEDULE_REFRESH_REQUESTED_KEY)) {
        localStorage.removeItem(FORCE_SCHEDULE_REFRESH_REQUESTED_KEY);
        localStorage.removeItem(scheduleKey);
        setSchedule([]);
      }
      fetchSchedule();
    }
  }, [addressId, streetName, addressLabel, selectedYearId, fetchSchedule]);

  const resetState = useCallback(() => {
    setCityId(null);
    setStreetId(null);
    setAddressId(null);
    setAddresses(null);
    setSchedule([]);
    localStorage.removeItem(scheduleKey);
    localStorage.removeItem(addressKey);
    localStorage.removeItem(addressIdKey);
  }, []);

  const handleYearChange = useCallback((yearId: string) => {
    setSelectedYearId(yearId);
    localStorage.setItem(SELECTED_YEAR_KEY, yearId);
    // Wyczyść cache harmonogramu i stan, żeby pobrać nowy dla wybranego roku
    localStorage.removeItem(scheduleKey);
    setSchedule([]); // Wyczyść stan harmonogramu natychmiast
    
    // Jeśli jest wybrany adres, pobierz harmonogram dla nowego roku natychmiast
    // yearId jest już ID roku z listy, więc możemy użyć go bezpośrednio
    if (addressId && yearId) {
      fetchScheduleForYear(addressId, yearId);
    }
  }, [addressId, fetchScheduleForYear]);

  return {
    isLoading,
    fullAddress,
    schedule,
    error,
    cityId,
    formattedCities,
    formattedStreets,
    addresses,
    formattedNumbers,
    setAddressId,
    setAddressLabel,
    setCityId,
    setStreetId,
    setStreetName,
    setAddresses,
    resetState,
    resetModalState,
    availableYears,
    selectedYearId,
    setSelectedYearId: handleYearChange,
  };
}
